
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut, Settings, Building2, ShieldCheck } from 'lucide-react';

export const UserMenu = () => {
  const { user, profile, signOut, isProfessional, isAdmin } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => navigate('/auth?tab=login')} className="text-sm">
          Se connecter
        </Button>
        <Button onClick={() => navigate('/auth?tab=signup')} className="bg-brand-primary hover:bg-brand-primary/90">
          S'inscrire
        </Button>
      </div>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    setIsDropdownOpen(false);
  };

  const handleNavigateToAccount = (tab: string) => {
    setIsDropdownOpen(false);
    // Force navigation even if on the same route
    setTimeout(() => {
      navigate(`/account?tab=${tab}`, { replace: false });
    }, 100);
  };

  // Fonction pour générer les initiales à partir du nom d'utilisateur
  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    } else if (profile?.first_name) {
      return `${profile.first_name[0]}`.toUpperCase();
    } else if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Fonction pour déterminer le nom à afficher
  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    } else if (profile?.first_name) {
      return profile.first_name;
    } else if (user?.email) {
      // Extraire le nom d'utilisateur de l'email (avant le @)
      return user.email.split('@')[0];
    }
    return 'Utilisateur';
  };

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
          <Avatar>
            <AvatarImage src={profile?.avatar_url || ''} />
            <AvatarFallback className="bg-brand-primary text-white">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => handleNavigateToAccount('dashboard')} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>Mon compte</span>
          </DropdownMenuItem>

          {isProfessional && (
            <DropdownMenuItem asChild>
              <Link to="/pro-dashboard" className="w-full cursor-pointer">
                <Building2 className="mr-2 h-4 w-4" />
                <span>Espace Pro</span>
              </Link>
            </DropdownMenuItem>
          )}

          {isAdmin && (
            <DropdownMenuItem asChild>
              <Link to="/admin" className="w-full cursor-pointer">
                <ShieldCheck className="mr-2 h-4 w-4" />
                <span>Administration</span>
              </Link>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={() => handleNavigateToAccount('profile')} className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            <span>Paramètres</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Se déconnecter</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
